import React, { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "./BlockDealsTSHistory.css";
import { environment } from "environment/environment";

const TABLE_COLUMN_COUNT = 6;

const formatApiDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const getDateFromToday = (days = 0) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - days);
    return date;
};

const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
};

const getLatestTradingDate = () => {
    const date = getDateFromToday(0);

    while (isWeekend(date)) {
        date.setDate(date.getDate() - 1);
    }

    return date;
};

export default function BlockDealsTSHistory() {

  /* ================= DATA ================= */
    const [bdlTSHistoryList, setBDLTSHistoryList] = useState([]);
    const [selectedDate, setSelectedDate] = useState(() => getLatestTradingDate());
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const maxSelectableDate = getLatestTradingDate();
    const formattedSelectedDate = selectedDate ? formatApiDate(selectedDate) : "";

    useEffect(() => {
        let isMounted = true;

        const fetchBlockDealsHistory = async () => {
            if (isMounted) {
                setLoading(true);
                setErrorMessage("");
            }

            try {
                const response = await axios.get(
                    `${environment.stockApiBaseUrl}${environment.blockDealsTopStockByDate}`,
                    {
                        params: {
                            date: formattedSelectedDate,
                        },
                    }
                );

                if (isMounted) {
                    setBDLTSHistoryList(response?.data?.data || []);
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error fetching block deals TS history:", error);

                if (isMounted) {
                    setBDLTSHistoryList([]);
                    setErrorMessage("Unable to fetch history data.");
                    setLoading(false);
                }
            }
        };

        fetchBlockDealsHistory();

        return () => {
            isMounted = false;
        };
    }, [formattedSelectedDate]);

     const formatValue = (value) => {
        if (value == null || isNaN(value)) return "--";

        const num = Number(value);
        const absNum = Math.abs(num);
        const sign = num < 0 ? "-" : "";


        // 🔥 Whole number (no decimal)
        if (Number.isInteger(num)) {
            return `${sign}${absNum.toLocaleString("en-IN")}`;
        }

        // 🔥 Default (Indian format with 2 decimal)
        return `${sign}${absNum.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const handleDateChange = (date) => {
        if (date && !isWeekend(date)) {
            setSelectedDate(date);
        }
    };

    const statusMessage = loading
        ? "Loading data..."
        : errorMessage || `No data found for ${formattedSelectedDate}.`;

    /* ================= UI ================= */

    return (
        <div className="block-deals-history">
            <div className="table-container">

                {/* ================= GAINERS ================= */}

                <div className="bdlts-history-header">
                    <h2 className="bdlts-history-heading">
                        <span className="sectorial-indices-span">BDLTS HISTORY</span>
                        <DatePicker
                            selected={selectedDate}
                            onChange={handleDateChange}
                            maxDate={maxSelectableDate}
                            filterDate={(date) => !isWeekend(date)}
                            dateFormat="dd-MM-yyyy"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={10}
                            scrollableYearDropdown
                            shouldCloseOnSelect
                            showPopperArrow={false}
                            popperPlacement="bottom-end"
                            portalId="bdlts-datepicker-portal"
                            className="bdlts-date-picker-input"
                            calendarClassName="bdlts-date-picker-calendar"
                            popperClassName="bdlts-date-picker-popper"
                        />
                    </h2>
                </div>
                <table className="responsive-table table-bordered text-center sticky-header stock-day-range-table">
                    <thead>
                         <tr>
                                <th>DEAL DATE</th>
                                <th>SYMBOL</th>
                                <th>TRADED PRICE</th>
                                <th>DEALS COUNT</th>
                                <th>DEAL VALUE & QUANTITY</th>
                                <th>SECTOR</th>
                                 
                            </tr>
                    </thead>

                     <tbody>
                            {bdlTSHistoryList?.length > 0 ? (
                                bdlTSHistoryList.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="deal-cell">

                                            <div className="deal-row">
                                                <span>{row.deal_date ?? "--"}</span>

                                            </div>
                                        </td>
                                        <td>
                                            <span className="span-color-price">
                                                {row.sc_nseid && row.sc_nseid.trim() !== ""
                                                    ? row.sc_nseid
                                                    : row.sc_comp || '--'}
                                            </span>
                                        </td>
                                        <td><span className="span-color-price">{formatValue(row.tradedPrice) ?? "--"}</span></td>
                                        <td><span>{row.dealsCount ?? "--"}</span></td>
                                        <td className="deal-cell deal-summary-cell">
                                            <div className="deal-summary">
                                                <span>Rs.</span>
                                                <span className="span-color">{formatValue(row.dealValue) ?? "--"}</span>
                                                <span>Crores</span>
                                                <span>NSE Block Trade For</span>
                                                <span className="span-color">{formatValue(row.quantity) ?? "--"}</span>
                                                <span>Shares</span>
                                            </div>
                                        </td>
                                        <td className="sector-cell">{row.stock_sector ?? "--"}</td>


                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={TABLE_COLUMN_COUNT}>{statusMessage}</td>
                                </tr>
                            )}
                        </tbody>

                        <tfoot className="fixed-footer">
                            <tr className="footer-left">
                                <td colSpan={TABLE_COLUMN_COUNT}>noptionwave © 2026</td>
                            </tr>
                        </tfoot>
                </table>

            </div>
        </div>
    );
}
